/**
 * TeleConsultation — full-screen video consult with AI granular report side panel
 * Doctor sees: live video + chat + AI report (auto-loaded from /api/ai/doctor-granular-report)
 * Pre-consult prep cut from 10-15 min to 30 sec.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Video, VideoOff, Mic, MicOff, Phone, MessageSquare,
  Sparkles, FileText, AlertTriangle, Brain, ShieldAlert, Pill,
  Activity, ClipboardList, Clock, Loader2, ChevronRight, X,
  RefreshCw, Maximize2, Minimize2
} from 'lucide-react';
import CameraDevicePicker from '../components/CameraDevicePicker';
import API_URL from '../config/api';

const SeverityBadge = ({ level }) => {
  const map = {
    urgent: 'bg-red-100 text-red-700 border-red-200',
    high: 'bg-orange-100 text-orange-700 border-orange-200',
    moderate: 'bg-amber-100 text-amber-700 border-amber-200',
    low: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  };
  return <span className={`px-2 py-0.5 text-[10px] font-bold uppercase rounded-md border ${map[level] || map.low}`}>{level || 'low'}</span>;
};

const Section = ({ icon: Icon, title, count, children, color = 'orange' }) => (
  <div className="border-b border-white/10 last:border-0">
    <div className="px-4 py-3 flex items-center justify-between sticky top-0 bg-slate-900/95 backdrop-blur z-10">
      <div className="flex items-center gap-2 text-sm font-bold text-white">
        <Icon className={`w-4 h-4 text-${color}-400`} />
        {title}
        {typeof count === 'number' && <span className="text-[10px] font-medium text-slate-400">· {count}</span>}
      </div>
    </div>
    <div className="px-4 pb-4 space-y-2">{children}</div>
  </div>
);

const TeleConsultation = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const patientId = searchParams.get('patient_id') || '';
  const patientEmail = searchParams.get('email') || '';
  const specialization = searchParams.get('spec') || 'general';

  const [stream, setStream] = useState(null);
  const [callActive, setCallActive] = useState(false);
  const [muted, setMuted] = useState(false);
  const [videoOff, setVideoOff] = useState(false);
  const [duration, setDuration] = useState(0);
  const [report, setReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState('');
  const [panelExpanded, setPanelExpanded] = useState(true);
  const [activeTab, setActiveTab] = useState('summary');
  const startedAtRef = useRef(null);

  // Auto-load AI report when component mounts
  const loadReport = useCallback(async () => {
    if (!patientId && !patientEmail) {
      setReportError('No patient context — pass ?patient_id=... or ?email=... in the URL');
      return;
    }
    setReportLoading(true); setReportError('');
    try {
      const res = await fetch(`${API_URL}/api/ai/doctor-granular-report`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          patient_id: patientId || undefined,
          email: patientEmail || undefined,
          specialization,
          include_ayurveda: true,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to fetch AI report');
      setReport(data);
    } catch (e) {
      setReportError(e.message);
    }
    setReportLoading(false);
  }, [patientId, patientEmail, specialization]);

  useEffect(() => { loadReport(); }, [loadReport]);

  // Call duration timer
  useEffect(() => {
    if (!callActive) return;
    startedAtRef.current = Date.now();
    const t = setInterval(() => setDuration(Math.floor((Date.now() - startedAtRef.current) / 1000)), 1000);
    return () => clearInterval(t);
  }, [callActive]);

  const formatTime = (s) => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;

  const handleStartCall = () => setCallActive(true);
  const handleEndCall = () => {
    if (stream) stream.getTracks().forEach(t => t.stop());
    setCallActive(false);
    setTimeout(() => navigate('/dashboard'), 1500);
  };

  const toggleMute = () => {
    if (!stream) return;
    stream.getAudioTracks().forEach(t => t.enabled = muted);
    setMuted(!muted);
  };
  const toggleVideo = () => {
    if (!stream) return;
    stream.getVideoTracks().forEach(t => t.enabled = videoOff);
    setVideoOff(!videoOff);
  };

  const ai = report?.ai_analysis || null;

  const tabs = [
    { id: 'summary', label: 'Summary', icon: Sparkles, count: ai?.evidence_synthesis?.length },
    { id: 'tests', label: 'Tests', icon: ClipboardList, count: ai?.follow_up_tests?.length },
    { id: 'drugs', label: 'Drugs', icon: Pill, count: ai?.drug_safety?.interactions?.length },
    { id: 'plan', label: 'Care Plan', icon: Activity, count: ai?.care_plan ? Object.values(ai.care_plan).flat().length : 0 },
    { id: 'flags', label: 'Red Flags', icon: ShieldAlert, count: ai?.red_flags?.length },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-white" data-testid="tele-consultation-page">
      {/* Header bar */}
      <div className="border-b border-white/10 px-4 py-3 flex items-center justify-between bg-slate-900/60 backdrop-blur">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center">
            <Video className="w-4 h-4 text-white" />
          </div>
          <div>
            <div className="font-bold text-sm">Tele Consultation</div>
            <div className="text-[11px] text-slate-400">{report?.patient_name || (patientId ? `Patient ${patientId.slice(0, 8)}` : 'Loading…')} · {specialization}</div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {callActive && (
            <div className="px-3 py-1 bg-red-500/20 border border-red-500/30 rounded-full flex items-center gap-2 text-xs">
              <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
              {formatTime(duration)}
            </div>
          )}
          <button onClick={() => navigate(-1)} className="p-2 hover:bg-white/10 rounded-lg" data-testid="close-tele-btn" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className={`grid ${panelExpanded ? 'lg:grid-cols-[1fr_420px]' : 'lg:grid-cols-[1fr_56px]'} gap-0 transition-all`}>
        {/* Video area */}
        <div className="p-4 lg:p-6 flex flex-col gap-4 min-h-[calc(100vh-64px)]">
          <div className="flex-1 rounded-2xl overflow-hidden bg-black relative" data-testid="tele-video-container">
            {!callActive ? (
              <div className="p-6 max-w-xl mx-auto h-full flex items-center">
                <div className="w-full bg-slate-900 rounded-2xl p-6 border border-white/10">
                  <div className="text-center mb-5">
                    <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-orange-500/10 text-orange-400 rounded-full text-[10px] font-bold uppercase tracking-widest mb-3">
                      <Video className="w-3 h-3" /> Pre-Call Setup
                    </div>
                    <h2 className="text-lg font-bold mb-1">Test camera & mic before joining</h2>
                    <p className="text-xs text-slate-400">Pick the right camera/mic — works with laptop webcams, USB cameras, and phone front/back cameras.</p>
                  </div>
                  <CameraDevicePicker
                    onStreamReady={(s) => setStream(s)}
                    onError={() => {}}
                  />
                  <button onClick={handleStartCall} disabled={!stream}
                    data-testid="start-call-btn"
                    className="w-full mt-5 py-3 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 disabled:opacity-40 disabled:cursor-not-allowed rounded-xl font-bold text-sm flex items-center justify-center gap-2">
                    <Phone className="w-4 h-4" /> Start Consultation
                  </button>
                </div>
              </div>
            ) : (
              <ActiveVideoView stream={stream} videoOff={videoOff} report={report} />
            )}
          </div>

          {/* Controls */}
          {callActive && (
            <div className="flex items-center justify-center gap-3" data-testid="tele-controls">
              <button onClick={toggleMute}
                className={`w-12 h-12 rounded-full flex items-center justify-center transition ${muted ? 'bg-red-500 hover:bg-red-600' : 'bg-white/10 hover:bg-white/15'}`}
                data-testid="toggle-mute-btn" aria-label={muted ? 'Unmute' : 'Mute'}>
                {muted ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
              </button>
              <button onClick={toggleVideo}
                className={`w-12 h-12 rounded-full flex items-center justify-center transition ${videoOff ? 'bg-red-500 hover:bg-red-600' : 'bg-white/10 hover:bg-white/15'}`}
                data-testid="toggle-video-btn" aria-label={videoOff ? 'Camera on' : 'Camera off'}>
                {videoOff ? <VideoOff className="w-5 h-5" /> : <Video className="w-5 h-5" />}
              </button>
              <button onClick={handleEndCall}
                className="w-14 h-12 rounded-full bg-red-500 hover:bg-red-600 flex items-center justify-center"
                data-testid="end-call-btn" aria-label="End call">
                <Phone className="w-5 h-5 rotate-[135deg]" />
              </button>
              <button className="w-12 h-12 rounded-full bg-white/10 hover:bg-white/15 flex items-center justify-center" aria-label="Chat">
                <MessageSquare className="w-5 h-5" />
              </button>
            </div>
          )}
        </div>

        {/* AI Report Side Panel */}
        <aside className={`bg-slate-900/70 border-l border-white/10 ${panelExpanded ? '' : 'overflow-hidden'}`} data-testid="ai-report-panel">
          {!panelExpanded ? (
            <button onClick={() => setPanelExpanded(true)}
              className="w-full h-full flex flex-col items-center justify-start pt-6 gap-4 hover:bg-white/5"
              aria-label="Expand AI panel" data-testid="expand-ai-panel">
              <Maximize2 className="w-4 h-4 text-orange-400" />
              <span className="text-[10px] font-bold uppercase tracking-widest text-orange-400" style={{ writingMode: 'vertical-rl' }}>AI Report</span>
            </button>
          ) : (
            <div className="h-full flex flex-col max-h-[calc(100vh-64px)]">
              {/* Panel header */}
              <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between sticky top-0 bg-slate-900/95 backdrop-blur z-20">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center">
                    <Sparkles className="w-3.5 h-3.5 text-white" />
                  </div>
                  <div>
                    <div className="text-sm font-bold">AI Granular Report</div>
                    <div className="text-[10px] text-slate-400">{specialization} · auto-generated</div>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={loadReport} className="p-1.5 hover:bg-white/10 rounded-lg" title="Refresh" data-testid="refresh-report-btn">
                    <RefreshCw className={`w-3.5 h-3.5 ${reportLoading ? 'animate-spin' : ''}`} />
                  </button>
                  <button onClick={() => setPanelExpanded(false)} className="p-1.5 hover:bg-white/10 rounded-lg" data-testid="collapse-ai-panel" aria-label="Collapse">
                    <Minimize2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>

              {/* Loading */}
              {reportLoading && (
                <div className="flex-1 flex flex-col items-center justify-center text-slate-400">
                  <Loader2 className="w-7 h-7 animate-spin text-orange-500 mb-3" />
                  <div className="text-xs">Synthesizing patient evidence…</div>
                </div>
              )}

              {/* Error */}
              {!reportLoading && reportError && (
                <div className="p-4">
                  <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-xl text-xs text-red-200 flex gap-2">
                    <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <div>{reportError}</div>
                  </div>
                </div>
              )}

              {/* Tabs + content */}
              {!reportLoading && !reportError && report && (
                <>
                  {/* Patient meta */}
                  <div className="px-4 py-3 border-b border-white/10 bg-white/[0.02]">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Patient</div>
                    <div className="font-bold text-sm">{report.patient_name}</div>
                    <div className="text-[11px] text-slate-400">
                      {report.data_sources && (
                        <>
                          {report.data_sources.wearable_readings} wearables ·
                          {' '}{report.data_sources.lab_reports} labs ·
                          {' '}{report.data_sources.prescriptions} Rx ·
                          {' '}{report.data_sources.vital_records} vitals
                        </>
                      )}
                    </div>
                    {ai?.data_quality && (
                      <div className="mt-2 flex items-center gap-2 text-[10px]">
                        <span className="text-slate-500">Data quality:</span>
                        <div className="flex-1 h-1.5 bg-slate-800 rounded-full overflow-hidden">
                          <div className="h-full bg-gradient-to-r from-orange-500 to-amber-500" style={{ width: `${ai.data_quality.completeness_score || 0}%` }} />
                        </div>
                        <span className="font-bold text-orange-400">{ai.data_quality.completeness_score || 0}/100</span>
                      </div>
                    )}
                  </div>

                  {/* Tab bar */}
                  <div className="flex overflow-x-auto border-b border-white/10 bg-white/[0.02] sticky top-[57px] z-10">
                    {tabs.map(t => (
                      <button key={t.id} onClick={() => setActiveTab(t.id)}
                        className={`px-3 py-2.5 text-[11px] font-bold uppercase tracking-wider flex items-center gap-1.5 whitespace-nowrap ${activeTab === t.id ? 'text-orange-400 border-b-2 border-orange-400' : 'text-slate-400 hover:text-white'}`}
                        data-testid={`ai-tab-${t.id}`}>
                        <t.icon className="w-3 h-3" />
                        {t.label}
                        {typeof t.count === 'number' && t.count > 0 && (
                          <span className="px-1.5 py-0.5 bg-orange-500/20 text-orange-400 rounded text-[9px]">{t.count}</span>
                        )}
                      </button>
                    ))}
                  </div>

                  {/* Tab content */}
                  <div className="flex-1 overflow-y-auto">
                    {activeTab === 'summary' && (
                      <>
                        {ai?.executive_summary && (
                          <div className="p-4 border-b border-white/10 bg-gradient-to-br from-orange-500/5 to-transparent">
                            <div className="text-[10px] font-bold text-orange-400 uppercase tracking-wider mb-1.5">Executive Summary</div>
                            <p className="text-xs leading-relaxed text-slate-200 italic">"{ai.executive_summary}"</p>
                          </div>
                        )}
                        <Section icon={Brain} title="Evidence Synthesis" count={ai?.evidence_synthesis?.length} color="blue">
                          {(ai?.evidence_synthesis || []).map((f, i) => (
                            <div key={i} className="p-3 bg-white/5 hover:bg-white/[0.07] rounded-lg border border-white/10">
                              <div className="flex items-start justify-between gap-2 mb-1.5">
                                <div className="text-xs font-semibold flex-1">{f.finding}</div>
                                <SeverityBadge level={f.severity} />
                              </div>
                              <div className="text-[10px] text-slate-400 flex flex-wrap gap-1 mt-2">
                                {(f.sources || []).map((s, j) => (
                                  <span key={j} className="px-1.5 py-0.5 bg-blue-500/15 text-blue-300 rounded font-mono">{s}</span>
                                ))}
                                {typeof f.confidence === 'number' && (
                                  <span className="ml-auto text-amber-400">{f.confidence}% conf.</span>
                                )}
                              </div>
                            </div>
                          ))}
                          {(!ai?.evidence_synthesis || ai.evidence_synthesis.length === 0) && <div className="text-xs text-slate-500 py-2">No synthesized findings.</div>}
                        </Section>

                        <Section icon={Brain} title="Differential Diagnoses" count={ai?.differential_diagnoses?.length} color="purple">
                          {(ai?.differential_diagnoses || []).map((d, i) => (
                            <div key={i} className="p-3 bg-white/5 rounded-lg border border-white/10">
                              <div className="flex items-center justify-between mb-1.5">
                                <div className="text-xs font-bold">{d.condition}</div>
                                <SeverityBadge level={d.likelihood === 'high' ? 'high' : d.likelihood === 'moderate' ? 'moderate' : 'low'} />
                              </div>
                              {d.supporting_evidence?.length > 0 && (
                                <ul className="text-[10px] text-slate-400 mt-1 space-y-0.5">
                                  {d.supporting_evidence.map((e, j) => <li key={j}>· {e}</li>)}
                                </ul>
                              )}
                              {d.rule_out_tests?.length > 0 && (
                                <div className="mt-2 flex flex-wrap gap-1">
                                  {d.rule_out_tests.map((t, j) => (
                                    <span key={j} className="px-1.5 py-0.5 bg-purple-500/15 text-purple-300 rounded text-[10px]">Rule out: {t}</span>
                                  ))}
                                </div>
                              )}
                            </div>
                          ))}
                          {(!ai?.differential_diagnoses || ai.differential_diagnoses.length === 0) && <div className="text-xs text-slate-500 py-2">No differentials identified.</div>}
                        </Section>

                        {ai?.ayurvedic_perspective?.dosha_imbalance && (
                          <Section icon={Sparkles} title="Ayurvedic Perspective" color="emerald">
                            <div className="p-3 bg-emerald-500/5 rounded-lg border border-emerald-500/20">
                              <div className="text-xs font-bold text-emerald-300 mb-1">{ai.ayurvedic_perspective.dosha_imbalance} imbalance</div>
                              <div className="text-[11px] text-slate-300 mb-2">{ai.ayurvedic_perspective.rationale}</div>
                              {ai.ayurvedic_perspective.complementary_therapies?.length > 0 && (
                                <div className="flex flex-wrap gap-1">
                                  {ai.ayurvedic_perspective.complementary_therapies.map((t, j) => (
                                    <span key={j} className="px-2 py-0.5 bg-emerald-500/15 text-emerald-300 rounded text-[10px]">{t}</span>
                                  ))}
                                </div>
                              )}
                            </div>
                          </Section>
                        )}
                      </>
                    )}

                    {activeTab === 'tests' && (
                      <Section icon={ClipboardList} title="Recommended Follow-Up Tests" count={ai?.follow_up_tests?.length} color="orange">
                        {(ai?.follow_up_tests || []).map((t, i) => (
                          <div key={i} className="p-3 bg-white/5 rounded-lg border border-white/10">
                            <div className="flex items-start justify-between gap-2 mb-1">
                              <div className="text-xs font-bold">{t.test}</div>
                              <span className={`px-2 py-0.5 text-[10px] font-bold uppercase rounded-md ${
                                t.urgency === 'immediate' ? 'bg-red-500/20 text-red-300 border border-red-500/30' :
                                t.urgency === 'within_72h' ? 'bg-orange-500/20 text-orange-300 border border-orange-500/30' :
                                t.urgency === 'within_2_weeks' ? 'bg-amber-500/20 text-amber-300 border border-amber-500/30' :
                                'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
                              }`}>{(t.urgency || '').replace(/_/g, ' ')}</span>
                            </div>
                            <div className="text-[11px] text-slate-300 mt-1.5">{t.rationale}</div>
                            {t.expected_yield && <div className="text-[10px] text-slate-500 mt-1.5 italic">Expected: {t.expected_yield}</div>}
                          </div>
                        ))}
                        {(!ai?.follow_up_tests || ai.follow_up_tests.length === 0) && <div className="text-xs text-slate-500 py-2">No follow-up tests suggested at this time.</div>}
                      </Section>
                    )}

                    {activeTab === 'drugs' && (
                      <>
                        <Section icon={Pill} title="Drug Interactions" count={ai?.drug_safety?.interactions?.length} color="red">
                          {(ai?.drug_safety?.interactions || []).map((it, i) => (
                            <div key={i} className="p-3 bg-red-500/5 rounded-lg border border-red-500/20">
                              <div className="flex items-center justify-between mb-1">
                                <div className="text-xs font-bold">{(it.drugs || []).join(' + ')}</div>
                                <SeverityBadge level={it.risk === 'severe' ? 'urgent' : it.risk === 'moderate' ? 'moderate' : 'low'} />
                              </div>
                              <div className="text-[10px] text-slate-400 mt-1">{it.mechanism}</div>
                              <div className="text-[10px] text-orange-300 mt-1.5 font-semibold">→ {it.action}</div>
                            </div>
                          ))}
                          {(!ai?.drug_safety?.interactions || ai.drug_safety.interactions.length === 0) && <div className="text-xs text-slate-500 py-2">No drug interactions detected ✓</div>}
                        </Section>

                        {ai?.drug_safety?.duplications?.length > 0 && (
                          <Section icon={Pill} title="Therapeutic Duplications" count={ai.drug_safety.duplications.length} color="amber">
                            {ai.drug_safety.duplications.map((d, i) => (
                              <div key={i} className="text-[11px] text-amber-300 px-3 py-2 bg-amber-500/5 rounded-lg border border-amber-500/20">{d}</div>
                            ))}
                          </Section>
                        )}

                        {ai?.drug_safety?.missing_indicated?.length > 0 && (
                          <Section icon={Pill} title="Suggested Additions" count={ai.drug_safety.missing_indicated.length} color="blue">
                            {ai.drug_safety.missing_indicated.map((d, i) => (
                              <div key={i} className="text-[11px] text-blue-300 px-3 py-2 bg-blue-500/5 rounded-lg border border-blue-500/20">{d}</div>
                            ))}
                          </Section>
                        )}
                      </>
                    )}

                    {activeTab === 'plan' && ai?.care_plan && (
                      <>
                        <Section icon={Clock} title="Immediate (72h)" count={ai.care_plan.immediate_72h?.length} color="red">
                          {(ai.care_plan.immediate_72h || []).map((p, i) => (
                            <div key={i} className="text-[11px] px-3 py-2 bg-red-500/5 rounded-lg border border-red-500/20 text-slate-200">{p}</div>
                          ))}
                          {(!ai.care_plan.immediate_72h || ai.care_plan.immediate_72h.length === 0) && <div className="text-xs text-slate-500 py-2">Nothing required in next 72h.</div>}
                        </Section>
                        <Section icon={Clock} title="Next 2 Weeks" count={ai.care_plan.next_2_weeks?.length} color="amber">
                          {(ai.care_plan.next_2_weeks || []).map((p, i) => (
                            <div key={i} className="text-[11px] px-3 py-2 bg-amber-500/5 rounded-lg border border-amber-500/20 text-slate-200">{p}</div>
                          ))}
                        </Section>
                        <Section icon={Clock} title="Ongoing" count={ai.care_plan.ongoing?.length} color="emerald">
                          {(ai.care_plan.ongoing || []).map((p, i) => (
                            <div key={i} className="text-[11px] px-3 py-2 bg-emerald-500/5 rounded-lg border border-emerald-500/20 text-slate-200">{p}</div>
                          ))}
                        </Section>
                        {ai.patient_communication_points?.length > 0 && (
                          <Section icon={MessageSquare} title="Talk Points (plain English)" count={ai.patient_communication_points.length} color="blue">
                            {ai.patient_communication_points.map((p, i) => (
                              <div key={i} className="text-[11px] px-3 py-2 bg-blue-500/5 rounded-lg border border-blue-500/20 text-slate-200 flex gap-2">
                                <ChevronRight className="w-3 h-3 text-blue-400 flex-shrink-0 mt-0.5" /> {p}
                              </div>
                            ))}
                          </Section>
                        )}
                      </>
                    )}

                    {activeTab === 'flags' && (
                      <Section icon={ShieldAlert} title="Red Flags" count={ai?.red_flags?.length} color="red">
                        {(ai?.red_flags || []).map((f, i) => (
                          <div key={i} className="p-3 bg-red-500/10 rounded-lg border border-red-500/30 flex gap-2 items-start">
                            <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                            <div className="text-[11px] text-red-100">{f}</div>
                          </div>
                        ))}
                        {(!ai?.red_flags || ai.red_flags.length === 0) && (
                          <div className="text-xs text-emerald-400 py-3 text-center">✓ No urgent red flags detected.</div>
                        )}
                      </Section>
                    )}

                    {/* AI error */}
                    {!ai && report?.ai_error && (
                      <div className="p-4">
                        <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-xl text-xs text-amber-200">
                          AI generation: {report.ai_error}
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          )}
        </aside>
      </div>
    </div>
  );
};

const ActiveVideoView = ({ stream, videoOff, report }) => {
  const localRef = useRef(null);
  useEffect(() => {
    if (localRef.current && stream) localRef.current.srcObject = stream;
  }, [stream]);
  return (
    <div className="relative h-full w-full bg-gradient-to-br from-slate-800 to-slate-900 flex items-center justify-center">
      {/* Remote (placeholder for HMS / Twilio integration) */}
      <div className="text-center text-slate-400 px-4">
        <div className="w-20 h-20 rounded-full bg-gradient-to-br from-orange-500 to-amber-500 mx-auto mb-3 flex items-center justify-center text-white font-bold text-2xl">
          {(report?.patient_name || 'P')[0]?.toUpperCase()}
        </div>
        <div className="text-base font-bold text-white">{report?.patient_name || 'Patient'}</div>
        <div className="text-xs mt-1">Connected · waiting for remote video stream</div>
        <div className="text-[10px] mt-2 text-slate-500">Plug in your HMS / Twilio Video token to enable peer video</div>
      </div>
      {/* Local PIP */}
      <div className="absolute bottom-4 right-4 w-40 md:w-56 aspect-video rounded-xl overflow-hidden border-2 border-white/20 shadow-2xl bg-black">
        {videoOff ? (
          <div className="h-full flex items-center justify-center text-slate-500 text-xs">Camera off</div>
        ) : (
          <video ref={localRef} autoPlay playsInline muted className="w-full h-full object-cover" />
        )}
      </div>
    </div>
  );
};

export default TeleConsultation;
